import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, ConfirmDialog, ModalFooter } from './Modal';
import { CLEAR_POPUP } from 'Application/Store/Actions/HistoryTypes';
import { ihm_viewer__dictionary, locale } from 'Application/i18n/i18nViewer';
import { parseAnnouncementEffects } from 'Ui/Components/AnnouncementEffects/parseAnnouncementEffects';
import { effectHandlers } from 'Ui/Components/AnnouncementEffects/effects';

/**
 * ModalContainer - Container reactif pour les modales via Redux
 * Remplace l'ancien système POPUP avec un vrai composant React
 */
export const ModalContainer = () => {
    const dispatch = useDispatch();
    const { popup, popupType } = useSelector(state => state.HistoryReducer);
    const [dontShowAgain, setDontShowAgain] = useState(false);

    useEffect(() => {
        setDontShowAgain(false);
    }, [popupType, popup?.dismissStorageKey]);

    const ProgressBody = ({ message }) => (
        <div className="modal-progress-wrap">
            <div className="modal-spinner" aria-hidden="true" />
            <p className="modal-progress-text">{message}</p>
        </div>
    );

    const handleClose = () => {
        dispatch({ type: CLEAR_POPUP });
    };

    const persistAnnouncementPreference = () => {
        if (!dontShowAgain || !popup?.dismissStorageKey) return;

        try {
            if (!popup?.dismissExpiresAt) {
                localStorage.removeItem(popup.dismissStorageKey);
                return;
            }

            localStorage.setItem(
                popup.dismissStorageKey,
                JSON.stringify({
                    dismissed: true,
                    expiresAt: popup.dismissExpiresAt,
                    maintenanceSignature: popup.dismissMaintenanceSignature
                })
            );
        } catch (error) {
            console.warn('[Announcements] Impossible de sauvegarder la préférence locale:', error);
        }
    };

    if (!popup) return null;

    const renderPopup = () => {
        if (!popup) return null;

        switch (popupType) {
            case 'confirmLeaveEditMode':
                return (
                    <Modal
                        isOpen={true}
                        onClose={handleClose}
                        title="Quitter le mode édition"
                        size="small"
                    >
                        <p className="confirm-message">{popup.message}</p>
                        <ModalFooter>
                            <button
                                className="modal-btn modal-btn-primary"
                                onClick={() => {
                                    handleClose();
                                    requestAnimationFrame(() => {
                                        popup.onConfirm?.();
                                    });
                                }}
                                type="button"
                            >
                                {popup.confirmText || 'Oui'}
                            </button>
                            <button
                                className="modal-btn modal-btn-cancel"
                                onClick={handleClose}
                                type="button"
                            >
                                {popup.cancelText || 'Non'}
                            </button>
                        </ModalFooter>
                    </Modal>
                );

            case 'confirmDeleteVersion':
                return (
                    <Modal
                        isOpen={true}
                        onClose={handleClose}
                        title="Supprimer la version"
                        size="small"
                    >
                        <p className="confirm-message">{popup.message}</p>
                        <ModalFooter>
                            <button
                                className="modal-btn modal-btn-danger"
                                onClick={() => {
                                    handleClose();
                                    requestAnimationFrame(() => {
                                        popup.onConfirm?.();
                                    });
                                }}
                                type="button"
                            >
                                {popup.confirmText || 'Supprimer'}
                            </button>
                            <button
                                className="modal-btn modal-btn-cancel"
                                onClick={handleClose}
                                type="button"
                            >
                                {popup.cancelText || 'Annuler'}
                            </button>
                        </ModalFooter>
                    </Modal>
                );

            case 'confirmCancelChanges':
                return (
                    <ConfirmDialog
                        isOpen={true}
                        onClose={handleClose}
                        onConfirm={popup.onConfirm}
                        title={ihm_viewer__dictionary.CANCEL_VERSION[locale()]}
                        message={popup.message}
                        confirmText={popup.confirmText || 'Oui'}
                        cancelText={popup.cancelText || 'Non'}
                    />
                );

            case 'collisionComputing':
                return (
                    <Modal
                        isOpen={true}
                        onClose={() => {}}
                        size="small"
                        closeOnBackdrop={false}
                        closeOnESC={false}
                    >
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <p>{popup.message || 'Calcul des collisions en cours...'}</p>
                        </div>
                    </Modal>
                );

            case 'announcement':
                const { cleanMessage, effects } = parseAnnouncementEffects(popup.message || '');
                const triggerEffects = () => {
                    effects.forEach(effectName => {
                        const handler = effectHandlers[effectName];
                        if (handler) handler();
                    });
                };
                return (
                    <Modal
                        isOpen={true}
                        onClose={() => {
                            persistAnnouncementPreference();
                            triggerEffects();
                            if (popup.onClose) {
                                popup.onClose();
                            } else {
                                handleClose();
                            }
                        }}
                        title={popup.title || 'Annonce'}
                        size="medium"
                    >
                        <p style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{cleanMessage}</p>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '16px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={dontShowAgain}
                                onChange={(event) => setDontShowAgain(event.target.checked)}
                            />
                            <span>
                                {ihm_viewer__dictionary.MAINTENANCE?.DONT_SHOW_AGAIN?.[locale()]
                                    || ihm_viewer__dictionary.MAINTENANCE?.DONT_SHOW_AGAIN?.en
                                    || 'Ne plus afficher cette annonce'}
                            </span>
                        </label>
                        <ModalFooter>
                            <button
                                className="modal-btn modal-btn-primary"
                                onClick={() => {
                                    persistAnnouncementPreference();
                                    triggerEffects();
                                    if (popup.onConfirm) {
                                        popup.onConfirm();
                                    } else {
                                        handleClose();
                                    }
                                }}
                                type="button"
                            >
                                {popup.confirmText || 'Continuer'}
                            </button>
                        </ModalFooter>
                    </Modal>
                );

            case 'saving':
                return (
                    <Modal
                        isOpen={true}
                        onClose={() => {}}
                        size="small"
                        closeOnBackdrop={false}
                        closeOnESC={false}
                    >
                        <ProgressBody message={popup.message || 'Sauvegarde en cours ...'} />
                    </Modal>
                );

            default:
                return (
                    <Modal
                        isOpen={true}
                        onClose={handleClose}
                        title={popup.title}
                        size={popup.size || 'medium'}
                    >
                        {popup.content}
                    </Modal>
                );
        }
    };

    return renderPopup();
};
